import moment from 'moment';
import { getFixedDateRanges } from '../components/ideas/filters/FixedDateRanges';

const RELATIVE_DAYS_KEY_REGEX = /^today([+-])(\d+)$/;

/**
 * Check whether a key matches the today+N / today-N pattern.
 */
export function isRelativeDaysKey(key: string): boolean {
  return RELATIVE_DAYS_KEY_REGEX.test(key);
}

/**
 * Resolve a "today+N" / "today-N" key to a concrete [start, end] date pair.
 * - today+7  --> [today 00:00, today+7 23:59]
 * - today-7  --> [today-7 00:00, today 23:59]
 * Returns null if the key does not match the pattern.
 */
export function resolveRelativeDaysKey(key: string): [Date, Date] | null {
  const match = key.match(RELATIVE_DAYS_KEY_REGEX);
  if (!match) return null;

  const sign = match[1];
  const offsetDays = parseInt(match[2], 10);

  const todayStart = moment().startOf('day').toDate();
  const todayEnd = moment().endOf('day').toDate();

  if (sign === '+') {
    const offsetEnd = moment().add(offsetDays, 'days').endOf('day').toDate();
    return [todayStart, offsetEnd];
  }

  const offsetStart = moment().subtract(offsetDays, 'days').startOf('day').toDate();
  return [offsetStart, todayEnd];
}

/**
 * Resolve any dynamic date key (preset or relative) to a concrete date range.
 * Returns null if the key is not recognized.
 */
export function resolveDynamicDateKey(key: string): [Date, Date] | null {
  const fixedRange = getFixedDateRanges[key];
  if (fixedRange?.values?.[0] && fixedRange?.values?.[1]) {
    return [fixedRange.values[0], fixedRange.values[1]];
  }
  return resolveRelativeDaysKey(key);
}

const PRESET_LABELS: Record<string, string> = {
  thisWeek: 'This Week',
  lastWeek: 'Last Week',
  nextWeek: 'Next Week',
  thisMonth: 'This Month',
  lastMonth: 'Last Month',
  nextMonth: 'Next Month',
  thisQuarter: 'This Quarter',
  lastQuarter: 'Last Quarter',
  nextQuarter: 'Next Quarter',
  thisYear: 'This Year',
  lastYear: 'Last Year',
  nextYear: 'Next Year',
};

/**
 * Convert a dynamic date key to a user-friendly label.
 * Handles both preset keys and relative day offset keys.
 */
export function formatDynamicDateLabel(key: string): string {
  if (PRESET_LABELS[key]) return PRESET_LABELS[key];

  const match = key.match(RELATIVE_DAYS_KEY_REGEX);
  if (match) {
    const sign = match[1];
    const days = parseInt(match[2], 10);
    if (days === 0) return 'Today';
    return sign === '+' ? `Next ${days} Days` : `Last ${days} Days`;
  }

  return key;
}

/**
 * Check if a filter uses any dynamic date or checkbox value
 * (preset keys, relative day offsets, or special checkbox keys).
 */
export function isDynamicDateOrFilterKey(key: string): boolean {
  if (PRESET_LABELS[key]) return true;
  if (isRelativeDaysKey(key)) return true;
  if (['unassigned', 'not_set', 'set'].includes(key)) return true;
  return false;
}
